import t from "@repo/localization/string.macro";
import { Transaction as CanadaTransactionPb } from "@repo/protobuf/gen/kikoff_canada/protobuf/models/transaction_pb";
import { FundingType as UsaFundingType } from "@repo/protobuf/gen/web/public/funds_pb";
import { Transaction as UsaTransactionPb } from "@repo/protobuf/gen/web/public/lending_pb";
import Protobuf from "@repo/protobuf/utils";
import Telemetry from "@repo/telemetry";
import Case from "@repo/utils/Case";
import Enum from "@repo/utils/Enum";
import UArray from "@repo/utils/UArray";
import UString from "@repo/utils/UString";

type Transaction = {
  id: Transaction.Id;
  parentId?: Transaction.Id;

  operation: Transaction.Operation;

  creditable: Transaction.Entity;
  debitable: Transaction.Entity;

  amountCents: number;
  transactedAt: Date;
};

namespace Transaction {
  export type Id = string & {};

  export type Operation =
    | "unknown"
    | "refund"
    | "payment"
    | "subscriptionActivation"
    | "purchase"
    | "subscriptionChange"
    | "promotionDiscount"
    | "paymentCorrection"
    | "courtesyCredit"
    | "adjustment";
  export namespace Operation {
    export const format = (() => {
      const map = {
        unknown: () => t`Unknown`,
        refund: () => t`Refund`,
        subscriptionActivation: () => t`Subscription activation`,
        payment: () => t`Payment`,
        purchase: () => t`Purchase`,
        subscriptionChange: () => t`Subscription change`,
        promotionDiscount: () => t`Promotion discount`,
        paymentCorrection: () => t`Payment correction`,
        courtesyCredit: () => t`Courtesy credit`,
        adjustment: () => t`Adjustment`,
      } satisfies Record<Operation, () => string>;

      return (operation: Operation | undefined) =>
        operation ? map[operation]() : t`None`;
    })();
  }

  export type Entity = {
    type: Entity.Type;
    id: string;
  };
  export namespace Entity {
    export type Type =
      | "paymentMethod"
      | "creditLine"
      | "loan"
      | "subscriptionInvoice"
      | "storeOrder"
      | "payoffFromKikoff"
      | "unknown";
  }
}

export default Transaction;

export namespace TransactionUsa {
  export const idFrom = (proto: UsaTransactionPb): Transaction.Id =>
    `${proto.creditToken}-${proto.debitToken}-${proto.amountCents}-${proto.transactedAt!.seconds}`;

  export const normalize = (proto: UsaTransactionPb): Transaction => ({
    id: idFrom(proto),
    parentId: proto.refund ? "isRefund" : undefined,
    amountCents: proto.amountCents,
    creditable: Entity.normalize(proto.creditToken, proto.creditType),
    debitable: Entity.normalize(proto.debitToken, proto.debitType),
    transactedAt: Protobuf.Timestamp.toDate(proto.transactedAt!),
    operation: Operation.normalize(proto.transactionTitle),
  });

  export namespace Entity {
    export const normalize = (
      token: string,
      fundingType: UsaFundingType
    ): Transaction.Entity => {
      const type = Case.fromConstant(
        Enum.keyOf(UsaFundingType, fundingType)
      ).toCamel();
      if (type === "unknown")
        Telemetry.captureError(
          new Error("Unexpected UNKNOWN type in transaction entity.")
        );

      return {
        id: token,
        // Probably don't need to support legacy entity types
        type: UArray.includesLiteral(["wallet", "amazonGiftCard"], type)
          ? "unknown"
          : type,
      };
    };
  }
  export namespace Operation {
    export const normalize = (() => {
      // TODO: Operations should be sent as an enum from the backend, for now we
      // map with error prone display names
      // See kikoff-us-backend/app/serializers/transactions/transaction_serializer.rb
      const operationByTitle: Record<string, Transaction.Operation> = {
        Payment: "payment",
        "Auto Payment": "payment",
        Purchase: "purchase",
        "Credit for Prior Payment": "paymentCorrection",
        "Payoff from Kikoff": "courtesyCredit",
        "Promo credit applied": "promotionDiscount",
        "Subscription change": "subscriptionChange",
        "Credit Service Pause Releif": "adjustment",
      };

      return (title: string): Transaction.Operation => {
        title = UString.trim.end(title, " Refund");
        return operationByTitle[title] || "unknown";
      };
    })();
  }
}

export namespace TransactionCanada {
  export const normalize = (proto: CanadaTransactionPb): Transaction => ({
    id: proto.id,
    parentId: proto.parentId,
    amountCents: proto.amountCents,
    creditable: Entity.normalize(proto.creditable!),
    debitable: Entity.normalize(proto.debitable!),
    transactedAt: Protobuf.Timestamp.toDate(proto.transactedAt!),
    operation: Operation.normalize(proto.operation),
  });

  export namespace Entity {
    export const normalize = (
      proto: CanadaTransactionPb.Transactable
    ): Transaction.Entity => {
      const type = Case.fromConstant(
        Enum.keyOf(CanadaTransactionPb.Transactable.Type, proto.type)
      ).toCamel();
      if (type === "unknown")
        throw new Error("Unexpected UNKNOWN type in transaction entity.");

      return {
        id: proto.id,
        type,
      };
    };
  }
  export namespace Operation {
    export const normalize = (
      proto: CanadaTransactionPb.Operation
    ): Transaction.Operation => {
      const operation = Case.fromConstant(
        Enum.keyOf(CanadaTransactionPb.Operation, proto)
      ).toCamel();

      // Make these more general to make it easier to map operations from other
      // adapters, need to add support in those other adapters if we want to
      // bring them back
      if (operation === "subscriptionDowngrade") return "subscriptionChange";
      if (operation === "storePurchase") return "purchase";
      return operation;
    };
  }
}
